/**
 * Fetching a legal document, and getting the markup out of whichever envelope
 * it arrives in.
 *
 * ⚠ Runs in the BROWSER. The authored href carries no host, so only a browser
 * can resolve it — `fetch("/dtvassets/…")` in Node has no origin to resolve
 * against and throws.
 *
 * ⚠ The fetchers never throw — every failure returns `null`. The Legal Modal
 * renders that as its unavailable state; In-Page Legal tells a failed request
 * (unavailable wording) from a loaded document with no plain field (nothing).
 */

import { safeHref } from "../../atoms/link/link";
import { hasMarkup } from "../../atoms/text/text";

/* A character reference — `&amp;`, `&#38;`, `&#x26;`. A bare `&`, as in AT&T, is not. */
const ENTITY = /&(?:#\d+|#x[0-9a-f]+|[a-z]+\d*);/i;

/**
 * Elements refused outright rather than rendered as authored, by the Legal
 * Modal's parser and by `stripRefused` for In-Page Legal.
 *
 * ⚠ Defence in depth, not the primary control — that is the same-origin fetch.
 * This is a legal-copy surface, so an executable node arriving through CMS copy
 * is worth refusing anyway. Authored `legal_content` is a trusted CMS field and
 * is not filtered; only fetched documents are.
 */
export const REFUSED: Readonly<Record<string, true>> = {
  script: true,
  style: true,
  iframe: true,
  object: true,
  embed: true,
  /* ⚠ These reach OUT of the legal copy and change the page around it: `base`
     rewrites every relative URL in the document, `meta http-equiv="refresh"`
     navigates it, `link` pulls in an external stylesheet, and `form` can post
     somewhere. None has any business in legal prose. */
  base: true,
  meta: true,
  link: true,
  form: true
};

/* In-Page Legal only. svg and math carry their own link/script surface and
   small print has no use for them; a <template>'s content escapes the
   attribute sweep. Not in REFUSED: the Legal Modal renders SVG and reads the
   <title> itself (toc.ts). */
const INLINE_REFUSED = ["svg", "math", "title", "template"];

/**
 * Shown by In-Page Legal when a legal document cannot be loaded. Verbatim from
 * the production component — not business-approved copy, matched so behaviour
 * does not change on the way over, and overridable. The Legal Modal carries the
 * error frame's own copy instead.
 */
export const UNAVAILABLE_TEXT = "Error while fetching data";

/** Stops a hung upstream from leaving the Legal Modal or In-Page Legal loading. */
const TIMEOUT_MS = 4000;

/**
 * The two fields a legal fragment can carry, in preference order. The `_toc`
 * variant is the one authored with headings — it is what makes an index
 * possible, so it wins wherever both are present. `extractLegalHtml` takes
 * `fields` to read a different list.
 */
const HTML_FIELDS = ["legalLong_text_toc", "legalLong_text"] as const;

/**
 * The untitled field only — what In-Page Legal shows in the page, where the
 * production block also reads `legalLong_text` and never the `_toc` variant.
 */
export const PLAIN_LEGAL_FIELDS = ["legalLong_text"] as const satisfies LegalFields;

/** Which fields to read, first non-empty wins — never empty. */
type LegalFields = readonly [LegalField, ...LegalField[]];
type LegalField = (typeof HTML_FIELDS)[number];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** First of `fields` on a `master` object that holds a non-empty string. */
function htmlFrom(master: unknown, fields: LegalFields): string | null {
  if (!isRecord(master)) {
    return null;
  }
  for (const field of fields) {
    const value = master[field];
    if (typeof value === "string" && value.trim() !== "") {
      return value;
    }
  }
  return null;
}

/**
 * The markup, out of either envelope the feeds use.
 *
 * AEM — the key under `contentFragment` is the fragment's own name, so it is
 * read positionally rather than by name:
 *
 *     { contentFragment: { <name>: { master: { legalLong_text_toc } } } }
 *
 * Storyblok — the same fields via the story's content. `Body` and `master` are
 * published as single-element arrays, but either can arrive as a bare object,
 * so both spellings are accepted:
 *
 *     { story: { content: { Body: [ { master: [ { legalLong_text } ] } ] } } }
 *
 * ⚠ Takes `unknown`: every level can be missing or the wrong type.
 */
export function extractLegalHtml(
  payload: unknown,
  fields: LegalFields = HTML_FIELDS
): string | null {
  if (!isRecord(payload)) {
    return null;
  }

  /* Storyblok first: a story response also carries no `contentFragment`, so the
     order only matters for a payload that somehow carried both. */
  const { story } = payload;
  if (isRecord(story)) {
    const content = isRecord(story.content) ? story.content : undefined;
    const body = first(content?.Body);
    const master = first(isRecord(body) ? body.master : undefined);
    const html = htmlFrom(master, fields);
    if (html) {
      return html;
    }
  }

  const fragment = payload.contentFragment;
  if (isRecord(fragment)) {
    for (const value of Object.values(fragment)) {
      const html = htmlFrom(isRecord(value) ? value.master : undefined, fields);
      if (html) {
        return html;
      }
    }
  }

  return null;
}

/** A single-element array's entry, or the value itself when it is not an array. */
function first(value: unknown): unknown {
  return Array.isArray(value) ? value[0] : value;
}

/* Arbitrary: only the path and query survive, so the origin is discarded. */
const BASE = "https://base.invalid";

/**
 * The authored href reduced to a path on our own origin. Keeping only
 * `pathname + search` is what makes leaving the origin impossible rather than
 * merely unlikely — no host survives, whatever was written.
 *
 * ⚠ EVERY value goes through the parser, including one that already starts
 * with `/`. Production short-circuits those, which is a hole: the URL parser
 * folds `\` into `/` for http(s), so `/\host/x` is protocol-relative and
 * escapes, as does `https://ours@host/x` through userinfo. CMS copy decides
 * this string and whatever returns renders as legal terms.
 */
export function toSameOriginPath(url: string): string {
  try {
    const { pathname, search } = new URL(url, BASE);
    return pathname + search;
  } catch {
    return url;
  }
}

export interface FetchLegalDocumentOptions {
  /** Milliseconds before the request is abandoned. */
  timeoutMs?: number;
  /** Swapped in tests. Defaults to the global. */
  fetchImpl?: typeof fetch;
}

/**
 * The legal document's markup, or `null` for every failure — an empty URL, a
 * non-2xx response, a body that is not JSON, an envelope with no markup, a
 * timeout. The `_toc` field wins where both are present.
 *
 * ⚠ The failures are deliberately not distinguished: the modal has one
 * unavailable state.
 *
 * The request is shared with In-Page Legal through fetchLegalPayload.
 */
export async function fetchLegalDocument(
  url: unknown,
  options: FetchLegalDocumentOptions = {}
): Promise<string | null> {
  const payload = await fetchLegalPayload(url, options);
  return payload === null ? null : extractLegalHtml(payload);
}

/* One request per document per fetch implementation (so an injected fetch never
   sees another's documents), for the page's lifetime, shared by In-Page Legal
   and the Legal Modal, which read different fields of the same payload. Loads
   the modal would show as unavailable are dropped, so reopening it asks again.
   ponytail: the modal also caches PARSED documents (legal-modal.tsx `cache`);
   this one only saves the request. Fold the two if memory matters. */
const loaded = new WeakMap<typeof fetch, Map<string, Promise<unknown>>>();

/**
 * The legal document's parsed JSON, or `null` when it could not be loaded — an
 * empty URL, a non-2xx response, a body that is not JSON, a timeout.
 *
 * In-Page Legal reads this rather than fetchLegalDocument because production
 * tells two cases apart: a request that fails shows the unavailable wording,
 * while a document that loads without the plain field shows nothing.
 *
 * Shared: callers asking for the same path through the same fetch get one
 * request and one payload (see `loaded`), so `timeoutMs` applies only to the
 * call that started it.
 */
export async function fetchLegalPayload(
  url: unknown,
  { timeoutMs = TIMEOUT_MS, fetchImpl = fetch }: FetchLegalDocumentOptions = {}
): Promise<unknown> {
  if (typeof url !== "string" || url.trim() === "" || typeof fetchImpl !== "function") {
    return null;
  }

  const path = toSameOriginPath(url.trim());
  let byPath = loaded?.get?.(fetchImpl);
  if (!byPath) {
    byPath = new Map<string, Promise<unknown>>();
    loaded?.set?.(fetchImpl, byPath);
  }
  const cached = byPath?.get?.(path);
  if (cached) {
    return cached;
  }
  const pending = requestLegalPayload(path, timeoutMs, fetchImpl)?.then?.(payload => {
    if (payload === null || extractLegalHtml(payload) === null) {
      byPath?.delete?.(path);
    }
    return payload;
  });
  byPath?.set?.(path, pending);
  return pending;
}

async function requestLegalPayload(
  path: string,
  timeoutMs: number,
  fetchImpl: typeof fetch
): Promise<unknown> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetchImpl(path, {
      method: "GET",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
      signal: controller.signal
    });

    if (!response.ok) {
      return null;
    }

    /* `response.json()` rather than parsing text: an HTML error page from an
       edge or a proxy rejects here rather than reaching the parser. */
    return (await response.json()) ?? null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/* Attributes that hold a destination, and so go through safeHref. Matches a
   namespaced name (`xlink:href`) too. */
const URL_ATTRIBUTE = /(?:^|:)(?:href|src|action|formaction)$/i;

/**
 * Fetched legal markup made safe for the Text atom, which parses markup
 * unfiltered (browser only). Removes REFUSED + INLINE_REFUSED, `on*` handlers
 * and unsafe URLs. Returns:
 * - "" when no visible text survives;
 * - `<span>`-wrapped when anything was removed, so Text decodes entities once —
 *   never `textContent`, which would decode `&lt;a&gt;` into a live element;
 * - entity-only copy `<span>`-wrapped with `<` / `>` escaped first;
 * - otherwise `html` untouched — re-serialising turns `AT&T` into `AT&amp;T`.
 */
export function stripRefused(html: string): string {
  /* The whole document, not just body: the parser hoists a leading script,
     style, meta, link, base or title into <head>. */
  const doc = new DOMParser().parseFromString(html, "text/html");
  let changed = false;

  doc?.querySelectorAll([...Object.keys(REFUSED), ...INLINE_REFUSED].join(","))?.forEach(node => {
    node?.remove?.();
    changed = true;
  });

  doc?.body?.querySelectorAll("*")?.forEach(node => {
    Array.from(node?.attributes ?? []).forEach(({ name, value }) => {
      if (/^on/i.test(name) || (URL_ATTRIBUTE.test(name) && safeHref(value) === "#")) {
        node?.removeAttribute?.(name);
        changed = true;
      }
    });
  });

  if (!doc?.body?.textContent?.trim()) {
    return "";
  }
  if (changed) {
    return `<span>${doc?.body?.innerHTML ?? ""}</span>`;
  }
  return !hasMarkup(html) && ENTITY.test(html) ?
    `<span>${(html ?? "").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</span>` :
    html;
}
